import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { AuditCard } from './AuditCard';
import type { Audit } from '@/types/asset-audit';

type GroupBy = 'none' | 'dueDate' | 'type' | 'priority' | 'status';

interface AuditsCardViewProps {
  audits: Audit[];
  onAuditClick: (auditId: string) => void;
  groupBy: GroupBy;
  onEdit?: (auditId: string) => void;
  onDuplicate?: (auditId: string) => void;
  onDelete?: (auditId: string) => void;
}

export function AuditsCardView({ 
  audits, 
  onAuditClick, 
  groupBy,
  onEdit,
  onDuplicate,
  onDelete
}: Readonly<AuditsCardViewProps>) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroupCollapse = (groupLabel: string) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupLabel)) {
      newCollapsed.delete(groupLabel);
    } else {
      newCollapsed.add(groupLabel);
    }
    setCollapsedGroups(newCollapsed);
  };

  const getGroupLabel = (audit: Audit, groupBy: GroupBy): string => {
    switch (groupBy) {
      case 'dueDate': {
        if (!audit.scheduledEndDate) return 'No Due Date';
        const dueDate = new Date(audit.scheduledEndDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        
        if (dueDate < today) return 'Overdue';
        if (dueDate.toDateString() === today.toDateString()) return 'Today';
        if (dueDate < nextWeek) return 'This Week';
        return 'Upcoming';
      }
      case 'type':
        return audit.type || 'Unknown Type';
      
      case 'priority':
        return audit.priority || 'No Priority';
      
      case 'status':
        return audit.status || 'No Status';
      
      default:
        return 'All Audits';
    }
  };

  const groupAudits = (audits: Audit[], groupBy: GroupBy): Record<string, Audit[]> => {
    if (groupBy === 'none') {
      return { 'All Audits': audits };
    }

    const groups: Record<string, Audit[]> = {};
    audits.forEach(audit => {
      const label = getGroupLabel(audit, groupBy);
      if (!groups[label]) {
        groups[label] = [];
      }
      groups[label].push(audit);
    });

    // Sort groups by priority for dueDate grouping
    if (groupBy === 'dueDate') {
      const order = ['Overdue', 'Today', 'This Week', 'Upcoming', 'No Due Date'];
      const sortedGroups: Record<string, Audit[]> = {};
      order.forEach(key => {
        if (groups[key]) {
          sortedGroups[key] = groups[key];
        }
      });
      return sortedGroups;
    }

    return groups;
  };

  const groupedAudits = groupAudits(audits, groupBy);

  if (!audits || audits.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-lg font-medium text-muted-foreground">No audits found</p>
          <p className="text-sm text-muted-foreground mt-1">
            Try adjusting your filters or create a new audit
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.entries(groupedAudits).map(([groupLabel, groupAudits]) => (
        <div key={groupLabel}>
          {/* Group Header */}
          {groupBy !== 'none' && (
            <button
              onClick={() => toggleGroupCollapse(groupLabel)}
              className="w-full flex items-center gap-3 mb-4 p-3 rounded-lg hover:bg-muted/50 transition-colors"
            >
              {collapsedGroups.has(groupLabel) ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronUp className="h-5 w-5 text-muted-foreground" />
              )}
              <h2 className="text-lg font-semibold">{groupLabel}</h2>
              <Badge variant="secondary">{groupAudits.length}</Badge>
            </button>
          )}
          
          {/* Group Content */}
          {!collapsedGroups.has(groupLabel) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {groupAudits.map((audit) => (
                <AuditCard
                  key={audit.id}
                  audit={audit}
                  onClick={() => onAuditClick(audit.id)}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
